// client/src/components/clientes/uploads/FotoPerfilUploader.js
import React, { useState, useEffect, useRef } from 'react';
import { useMutateClientes } from '../../../hooks/useClientes';
import { useQueryClient } from '@tanstack/react-query'; // Importar useQueryClient
import { UploadCloud, Loader2, Eye, RefreshCw } from 'lucide-react';
import PropTypes from 'prop-types';
import ImageZoomModal from '../../common/ImageZoomModal';
import PlaceholderGenerator from '../../../utils/placeholders';

const FotoPerfilUploader = ({ cliente, onUploadSuccess }) => {
    const { uploadFotoPerfil, isPending: loading } = useMutateClientes();
    const queryClient = useQueryClient(); // ✅ Adicionar queryClient
    
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [zoomImage, setZoomImage] = useState(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [showIniciais, setShowIniciais] = useState(true);
    const [iniciais, setIniciais] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [ultimaAtualizacao, setUltimaAtualizacao] = useState(Date.now());
    
    // Usar ref para detectar mudanças no cliente
    const clienteRef = useRef();
    const [clienteAtual, setClienteAtual] = useState(cliente);

    // ✅ Função para atualizar dados do cliente
    const atualizarClienteLocalmente = (novosDados) => {
        console.log('🔄 Atualizando dados do cliente localmente:', novosDados);
        setClienteAtual(prev => ({
            ...prev,
            user: {
                ...prev?.user,
                ...novosDados.user,
                photo: novosDados.user?.photo || novosDados.photo,
                photoUrl: novosDados.user?.photoUrl || novosDados.photoUrl || novosDados.user?.photo
            }
        }));
        setUltimaAtualizacao(Date.now());
    };

    // ✅ Função para forçar recarregamento dos dados do cliente
    const recarregarCliente = async () => {
        if (!cliente?._id) return;
        
        setIsRefreshing(true);
        try {
            console.log('🔄 Forçando recarregamento do cliente ID:', cliente._id);
            
            // Invalidar queries do cliente para forçar recarregamento
            await queryClient.invalidateQueries({
                queryKey: ['cliente', cliente._id]
            });
            
            // Aguardar um pouco para queries serem refetchadas
            setTimeout(() => {
                setIsRefreshing(false);
                console.log('✅ Recarregamento forçado concluído');
            }, 500);
            
        } catch (error) {
            console.error('❌ Erro ao recarregar cliente:', error);
            setIsRefreshing(false);
        }
    };

    // Função para formatar o nome para exibição
    const formatarNomeExibicao = (nomeCompleto) => {
        if (!nomeCompleto) return 'Consumidor Final';
        const nomeLimpo = nomeCompleto.replace(/#[^\s]*/g, '').trim();
        return nomeLimpo || 'Consumidor Final';
    };

    useEffect(() => {
        // Se o cliente prop mudou, atualizar estado local
        if (cliente !== clienteRef.current) {
            clienteRef.current = cliente;
            setClienteAtual(cliente);
        }
    }, [cliente]);

    useEffect(() => {
        console.log('📸 Avatar - Dados atualizados:', {
            clienteAtual,
            ultimaAtualizacao
        });

        const userPhoto = clienteAtual?.user?.photo;
        const photoUrl = clienteAtual?.user?.photoUrl;
        const nomeCompleto = clienteAtual?.user?.name || 'Consumidor Final';
        const email = clienteAtual?.user?.email || '';

        // Calcula as iniciais
        const initials = PlaceholderGenerator.getInitials(nomeCompleto, email);
        setIniciais(initials);

        console.log('📸 Avatar - Configuração de imagem:', {
            nome: nomeCompleto,
            iniciais: initials,
            userPhoto,
            photoUrl,
            temUserPhoto: !!userPhoto,
            temPhotoUrl: !!photoUrl
        });

        // ORDEM DE PRIORIDADE:
        // 1. photoUrl (se não for placeholder)
        // 2. userPhoto (se não for placeholder)
        // 3. Exibir apenas iniciais

        const temFotoReal = 
            (photoUrl && !PlaceholderGenerator.isPlaceholderUrl(photoUrl)) ||
            (userPhoto && !PlaceholderGenerator.isPlaceholderUrl(userPhoto));

        if (temFotoReal) {
            // Tem foto real - usa a foto
            const fotoParaUsar = (photoUrl && !PlaceholderGenerator.isPlaceholderUrl(photoUrl)) 
                ? photoUrl 
                : userPhoto;
            
            console.log('✅ Usando foto real:', fotoParaUsar);
            
            // Se a foto mudou, atualiza o preview
            if (fotoParaUsar !== preview) {
                console.log('🔄 Atualizando preview para nova foto:', fotoParaUsar);
                setPreview(fotoParaUsar);
                setShowIniciais(false);
            }
        } else {
            // NÃO tem foto real - mostra apenas iniciais
            console.log('🎨 Exibindo apenas iniciais:', initials);
            setPreview(null);
            setShowIniciais(true);
        }
    }, [clienteAtual, ultimaAtualizacao]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            console.log('📄 Arquivo selecionado:', selectedFile.name);
            setFile(selectedFile);
            
            // Preview temporário do arquivo
            const objectUrl = URL.createObjectURL(selectedFile);
            setPreview(objectUrl);
            setShowIniciais(false);
            
            // Limpar preview temporário quando componente desmontar
            return () => {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
            };
        }
    };

    const handleUpload = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!file || !cliente?._id) return;
        
        console.log('⬆️ Iniciando upload para cliente:', cliente._id);
        
        uploadFotoPerfil.mutate({ clienteId: cliente._id, file: file }, {
            onSuccess: (responseData) => {
                console.log('✅ Upload bem-sucedido - Resposta completa:', responseData);
                setFile(null);
                
                const fileInput = document.getElementById('fotoPerfilInput');
                if (fileInput) {
                    fileInput.value = '';
                }
                
                // ✅ CORREÇÃO CRÍTICA: Extrair dados da resposta corretamente
                const data = responseData?.data || responseData;
                
                console.log('🔄 Novos dados após upload:', {
                    responseData,
                    data,
                    hasPhotoUrl: !!data?.photoUrl,
                    hasUserPhotoUrl: !!data?.user?.photoUrl,
                    hasUserPhoto: !!data?.user?.photo,
                    userData: data?.user
                });
                
                // Atualiza com os novos dados - múltiplos formatos possíveis
                const newPhotoUrl = data?.photoUrl || data?.user?.photoUrl;
                const newPhoto = data?.photo || data?.user?.photo;
                
                // ✅ CORREÇÃO: Atualizar cliente localmente imediatamente
                if (newPhotoUrl || newPhoto) {
                    console.log('🔄 Atualizando preview localmente com:', { newPhotoUrl, newPhoto });
                    
                    // Usar a URL mais atual
                    const fotoParaUsar = newPhotoUrl || newPhoto;
                    
                    if (fotoParaUsar && !PlaceholderGenerator.isPlaceholderUrl(fotoParaUsar)) {
                        // Forçar recarregamento da imagem com timestamp para evitar cache
                        const timestamp = Date.now();
                        const fotoComCacheBuster = `${fotoParaUsar}?t=${timestamp}`;
                        
                        console.log('🔄 Definindo preview com cache buster:', fotoComCacheBuster);
                        setPreview(fotoComCacheBuster);
                        setShowIniciais(false);
                        
                        // Atualizar dados do cliente localmente
                        atualizarClienteLocalmente({
                            user: {
                                ...clienteAtual?.user,
                                photo: newPhoto,
                                photoUrl: newPhotoUrl
                            }
                        });
                    }
                }
                
                // ✅ FORÇAR RECARREGAMENTO DOS DADOS DO CLIENTE
                setTimeout(() => {
                    recarregarCliente();
                }, 300);
                
                // ✅ INVALIDAR CACHE DA LISTA DE CLIENTES TAMBÉM
                setTimeout(() => {
                    queryClient.invalidateQueries({
                        queryKey: ['clientes']
                    });
                    console.log('✅ Cache da lista de clientes invalidado');
                }, 500);
                
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
                
                // ✅ MOSTRAR MENSAGEM DE SUCESSO
                console.log('🎉 Upload concluído com sucesso! A foto será atualizada em instantes.');
            },
            onError: (error) => {
                console.error('❌ Erro no upload:', {
                    error,
                    response: error.response,
                    data: error.response?.data
                });
                
                // Se o erro foi no upload, mas a foto foi salva, tentar recarregar de qualquer forma
                if (error.response?.status === 200 || error.response?.status === 201) {
                    console.log('⚠️ Erro na resposta, mas tentando recarregar dados...');
                    setTimeout(() => {
                        recarregarCliente();
                    }, 500);
                }
            }
        });
    };

    const handleZoomFoto = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!preview) return;
        
        setZoomImage({
            url: preview,
            nomeOriginal: `Foto de ${clienteAtual?.user?.name || 'Cliente'}`
        });
        setIsZoomOpen(true);
    };

    const handleSelectImage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        document.getElementById('fotoPerfilInput').click();
    };

    // ✅ Função para recarregar manualmente
    const handleManualRefresh = () => {
        recarregarCliente();
    };

    // Obtém informações para exibição
    const nomeCompleto = clienteAtual?.user?.name || 'Consumidor Final';
    const nomeExibicao = formatarNomeExibicao(nomeCompleto);
    
    // Só é placeholder quando não tem preview (está mostrando iniciais)
    const isPlaceholder = !preview && showIniciais;

    console.log('📊 Estado atual do FotoPerfilUploader:', {
        preview,
        showIniciais,
        iniciais,
        isPlaceholder,
        nomeExibicao,
        clienteAtual: clienteAtual?._id,
        userPhoto: clienteAtual?.user?.photo,
        userPhotoUrl: clienteAtual?.user?.photoUrl,
        loading,
        isRefreshing
    });

    return (
        <>
            <div className="foto-perfil-uploader" onClick={(e) => e.stopPropagation()}>
                <div className="upload-header">
                    <h4 className="upload-title">Foto de Perfil</h4>
                    <button
                        type="button"
                        onClick={handleManualRefresh}
                        disabled={isRefreshing}
                        className="refresh-button"
                        title="Recarregar foto"
                    >
                        {isRefreshing ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <RefreshCw size={16} />
                        )}
                    </button>
                </div>
                
                <div className="avatar-preview">
                    {preview ? (
                        // TEM FOTO REAL - exibe a foto
                        <div className="gallery-image-container">
                            <img 
                                src={preview} 
                                alt={`Foto de ${nomeExibicao}`}
                                className="gallery-image"
                                onClick={handleZoomFoto}
                                style={{ cursor: 'pointer' }}
                                onError={(e) => {
                                    // Se a foto não carregar, volta para iniciais
                                    console.error('❌ Erro ao carregar foto:', preview);
                                    e.target.onerror = null;
                                    setPreview(null);
                                    setShowIniciais(true);
                                }}
                                key={preview} // ✅ Forçar recarregamento quando preview mudar
                            />
                            <button
                                type="button"
                                onClick={handleZoomFoto}
                                className="gallery-zoom-btn"
                                title="Ampliar foto"
                            >
                                <Eye size={16} />
                            </button>
                        </div>
                    ) : showIniciais ? (
                        // NÃO TEM FOTO - exibe APENAS INICIAIS
                        <div className="avatar-placeholder-wrapper">
                            <div className="initials-display">
                                {iniciais}
                            </div>
                        </div>
                    ) : (
                        // ESTADO INICIAL (carregando)
                        <div className="avatar-placeholder-wrapper">
                            <div className="initials-display">
                                <Loader2 size={24} className="animate-spin" />
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="upload-controls">
                    <input 
                        type="file" 
                        id="fotoPerfilInput" 
                        accept="image/*" 
                        onChange={handleFileChange} 
                        style={{ display: 'none' }} 
                        disabled={loading || isRefreshing}
                    />
                    <label 
                        htmlFor="fotoPerfilInput" 
                        className={`action-button action-button-secondary ${loading || isRefreshing ? 'is-disabled' : ''}`}
                        onClick={handleSelectImage}
                    >
                        Selecionar Imagem
                    </label>
                    <button 
                        onClick={handleUpload} 
                        disabled={!file || loading || isRefreshing} 
                        className="action-button action-button-primary"
                    >
                        {loading ? (
                            <Loader2 size={18} className="animate-spin icon" />
                        ) : (
                            <UploadCloud size={18} className="icon" />
                        )}
                        Enviar Foto
                    </button>
                </div>
                
                {file && (
                    <div className="file-info">
                        <small>Arquivo selecionado: {file.name} ({(file.size / 1024).toFixed(1)} KB)</small>
                    </div>
                )}
                
                {preview && (
                    <div className="zoom-hint">
                        💡 Clique na foto para ampliar
                    </div>
                )}
                
                {!preview && showIniciais && (
                    <div className="placeholder-info">
                        <small>Adicione uma foto para {nomeExibicao}</small>
                    </div>
                )}
                
                {(loading || isRefreshing) && (
                    <div className="upload-status">
                        <Loader2 size={14} className="animate-spin mr-2" />
                        <small>{loading ? 'Enviando foto...' : 'Atualizando dados...'}</small>
                    </div>
                )}
            </div>

            <ImageZoomModal
                image={zoomImage}
                isOpen={isZoomOpen}
                onClose={() => setIsZoomOpen(false)}
            />
        </>
    );
};

FotoPerfilUploader.propTypes = {
    cliente: PropTypes.shape({
        _id: PropTypes.string,
        user: PropTypes.shape({
            _id: PropTypes.string,
            name: PropTypes.string,
            email: PropTypes.string,
            photo: PropTypes.string,
            photoUrl: PropTypes.string
        })
    }),
    onUploadSuccess: PropTypes.func
};

FotoPerfilUploader.defaultProps = {
    cliente: null,
    onUploadSuccess: () => {}
};

export default FotoPerfilUploader;